import { useAuth } from '@teko/auth-react';
import { useLoading } from '@teko/ds-desktop';
import { ISupermarketTerminal } from '@teko/order';
import { trackCustomEvent } from '@teko/tracking';
import { Address } from '@teko/user';
import TerraKit from '@terra-js/terra-kit';
import { PopupTerminalPicker } from 'components/Terminal';
import { useGlobalData } from 'contexts/GlobalDataContext';
import { useServices } from 'contexts/ServiceContext';
import {
  clearCacheAndReloadPage,
  getJson,
  getLocalStorageValue,
  getUserSelectedInfo,
  reloadPage,
  removeUserSelectedInfo,
  setLocalStorageValue,
  setUserSelectedInfo,
} from 'helpers';
import { NumberParam, StringParam, getQueryParams } from 'hooks/useQuery';
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { userSelectedConstants } from '../../constants';
import { logger } from '@teko/log';
import { isEmptyObject } from '@teko/utils';

export enum MODE_TERMINAL_PICKER {
  ENTER_ADDRESS = 'ENTER_ADDRESS',
  LIST_TERMINAL = 'LIST_TERMINAL',
  LIST_ADDRESS = 'LIST_ADDRESS',
}

export interface DistanceTerminalContext {
  currentTerminal?: ISupermarketTerminal;
  currentTerminals?: ISupermarketTerminal[];
  location?: Location;
  setLoadingGetDistanceTerminals?: (loading: boolean) => void;
  isLoadingGetDistanceTerminals?: boolean;
  setLocationGetDistanceTerminals?: (location: Location) => void;
  locationGetDistanceTerminals?: Location;
  isGetCurrentLocation?: boolean;
  setIsGetCurrentLocation?: (isGetCurrentLocation: boolean) => void;
  isModeEnterAddress?: boolean;
  setIsModeEnterAddress?: (isModeEnterAddress: boolean) => void;

  modeTerminalPicker?: MODE_TERMINAL_PICKER;
  setModeTerminalPicker: (modeTerminalPicker: MODE_TERMINAL_PICKER) => void;

  showTerminalPicker?: boolean;
  onCloseTerminalPicker?: () => void;
  onOpenTerminalPicker?: () => void;
  getDistanceLocation?: (addressInfo: {
    address?: string;
    lat?: number;
    long?: number;
  }) => Promise<ISupermarketTerminal[]>;
  setNewTerminal?: (
    terminal: ISupermarketTerminal,
    noNeedReload?: boolean,
    reloadUrl?: string,
  ) => void;
  messageError?: string;
  setMessageError?: Dispatch<SetStateAction<string>>;
  setLoadingGetDistanceTerminal?: (loading: boolean) => void;
  overrideTerminal?: (
    location: Location,
    terminal: ISupermarketTerminal,
    terminals: ISupermarketTerminal[],
    noNeedReload?: boolean,
    reloadUrl?: string,
  ) => void;
  overrideLocation?: (location: Location) => void;
  resetSelectedAddress?: (reloadUrl?: string) => void;
  overrideTerminals?: (terminals: ISupermarketTerminal[]) => void;
  permissionGetLocationStatus?: string;
  temporaryDataDistanceTerminal: TemporaryDataTerminalPicker;
  setTemporaryDataDistanceTerminal?: Dispatch<
    SetStateAction<TemporaryDataTerminalPicker>
  >;
  onEditAddress?: () => void;
  loadingGetDataFromStorage?: boolean;
  editingAddress?: Address;
  setEditingAddress: (address?: Address) => void;
}
export interface Location {
  address: string;
  provinceCode?: string;
  districtCode?: string;
  wardCode?: string;
  provinceName: string;
  wardName: string;
  districtName?: string;
  lat?: number;
  long?: number;
  addressNote?: string;
  addressId?: string;
}

export interface TemporaryDataTerminalPicker {
  location: Location;
  terminals: ISupermarketTerminal[];
}

export const defaultTemporaryData = {
  location: {
    address: '',
    provinceCode: '',
    wardCode: '',
    provinceName: '',
    wardName: '',
    districtCode: '',
    districtName: '',
    addressNote: '',
  },
  terminals: [],
};

const DistanceTerminalContext = createContext<DistanceTerminalContext>({
  temporaryDataDistanceTerminal: defaultTemporaryData,
  setModeTerminalPicker: () => {},
  setEditingAddress: () => {},
});

interface DistanceTerminalProviderProps {
  children: React.ReactNode;
}

export const DistanceTerminalProvider = ({
  children,
}: DistanceTerminalProviderProps) => {
  const {
    epicOmni1825,
    isDmsFlow,
    platformId,
    isMobile,
    clearCacheAfterSwitchLocation,
    isAllowGetTerminalFromTerra,
    terminal,
    terminalId,
    terminalName,
    epicOmni1936,
  } = useGlobalData();
  const { orderService, cartServiceV2 } = useServices();
  const { user } = useAuth();

  const [, setLoadingGetDistanceTerminal] = useLoading('distanceTerminal');
  const [isModeEnterAddress, setIsModeEnterAddress] = useState(true);
  const [loadingGetDataFromStorage, setLoadingGetDataFromStorage] =
    useState(false);

  const [currentTerminals, setCurrentTerminals] = useState<
    ISupermarketTerminal[]
  >([]);
  const [showTerminalPicker, setShowTerminalPicker] = useState<boolean>(false);

  // UX4BUYER-9425: extractTenant đã đọc terminal / terminal_id / terminal_name từ
  // cookie của request nên server biết siêu thị đang chọn. Seed vào state để SSR vẽ
  // luôn tên siêu thị thay cho empty state "Chọn siêu thị giao hàng" — chính empty
  // state này làm header nhảy layout sau khi getAndSaveTerminalFromStorage chạy xong.
  // Cookie chỉ đủ field để hiển thị — effect bên dưới vẫn ghi đè bằng object đầy đủ
  // lấy từ localStorage sau khi hydrate.
  const terminalFromCookie: ISupermarketTerminal | undefined =
    !!terminalName && typeof terminal === 'string' && !!terminal
      ? {
          terminalCode: terminal,
          terminalId: Number(terminalId) || 0,
          terminalName,
          siteId: 0,
          fullAddress: '',
          distance: 0,
          provinceCode: '',
        }
      : undefined;

  // epic omni 1825
  const [currentTerminal, setCurrentTerminal] = useState<
    ISupermarketTerminal | undefined
  >(terminalFromCookie);
  const [loading, setLoading] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('denied');
  const [location, setLocation] = useState<Location>({
    address: '',
    provinceCode: '',
    wardCode: '',
    provinceName: '',
    wardName: '',
    districtCode: '',
    districtName: '',
  });

  const [modeTerminalPicker, setModeTerminalPicker] =
    useState<MODE_TERMINAL_PICKER>();
  const [editingAddress, setEditingAddress] = useState<Address | undefined>(
    undefined,
  );
  const [isGetCurrentLocation, setIsGetCurrentLocation] = useState(false);
  const [temporaryData, setTemporaryData] =
    useState<TemporaryDataTerminalPicker>(defaultTemporaryData);
  const [messageError, setMessageError] = useState('');

  const onOpenTerminalPicker = () => {
    setShowTerminalPicker(true);
    setMessageError('');
  };

  const onCloseTerminalPicker = () => {
    if (!currentTerminal) {
      return;
    }
    setShowTerminalPicker(false);
  };

  const selectTerminal = (terminal: ISupermarketTerminal) => {
    setUserSelectedInfo({
      key: userSelectedConstants.TERMINAL,
      value: terminal.terminalCode,
      isMobile,
      isNeedEncodeCookie: true,
      flags: { epicOmni1936 },
    });
    setUserSelectedInfo({
      key: userSelectedConstants.TERMINAL_NAME,
      value: terminal.terminalName,
      isMobile,
      isNeedEncodeCookie: true,
      flags: { epicOmni1936 },
    });
    setUserSelectedInfo({
      key: userSelectedConstants.TERMINAL_ID,
      value: terminal?.terminalId?.toString(),
      isMobile,
      isNeedEncodeCookie: true,
      flags: { epicOmni1936 },
    });
    setLocalStorageValue(
      userSelectedConstants.USER_LOCATION,
      JSON.stringify(temporaryData.location),
      isMobile,
    );
    setLocalStorageValue(
      userSelectedConstants.TERMINALS,
      JSON.stringify(temporaryData.terminals),
      isMobile,
    );

    setUserSelectedInfo({
      key: userSelectedConstants.IS_GET_GEOLOCATION,
      value: JSON.stringify(isGetCurrentLocation),
      isMobile,
      isNeedEncodeCookie: true,
      flags: { epicOmni1936 },
    });
    setCurrentTerminal(terminal);
  };

  const overrideLocation = (location: Location) => {
    setLocalStorageValue(
      userSelectedConstants.USER_LOCATION,
      JSON.stringify(location),
      isMobile,
    );
    setLocation(location);
  };

  // clear the currently selected address/terminal (context + localStorage + cookie), then reload
  const resetSelectedAddress = (reloadUrl?: string) => {
    removeUserSelectedInfo(userSelectedConstants.USER_LOCATION, isMobile);
    removeUserSelectedInfo(userSelectedConstants.TERMINAL, isMobile);
    removeUserSelectedInfo(userSelectedConstants.TERMINAL_NAME, isMobile);
    removeUserSelectedInfo(userSelectedConstants.TERMINAL_ID, isMobile);
    removeUserSelectedInfo(userSelectedConstants.TERMINALS, isMobile);
    removeUserSelectedInfo(userSelectedConstants.IS_GET_GEOLOCATION, isMobile);
    setLocation({
      address: '',
      provinceCode: '',
      wardCode: '',
      provinceName: '',
      wardName: '',
      districtCode: '',
      districtName: '',
    });
    setCurrentTerminal(undefined);
    setCurrentTerminals([]);
    reloadPage(reloadUrl);
  };

  const overrideTerminals = (terminals: ISupermarketTerminal[]) => {
    setLocalStorageValue(
      userSelectedConstants.TERMINALS,
      JSON.stringify(terminals),
      isMobile,
    );
    setCurrentTerminals(terminals);
  };

  const overrideTerminal = async (
    location: Location,
    terminal: ISupermarketTerminal,
    terminals: ISupermarketTerminal[],
    noNeedReload?: boolean,
    reloadUrl?: string,
  ) => {
    setUserSelectedInfo({
      key: userSelectedConstants.TERMINAL,
      value: terminal.terminalCode,
      isMobile,
      isNeedEncodeCookie: true,
      flags: { epicOmni1936 },
    });
    setUserSelectedInfo({
      key: userSelectedConstants.TERMINAL_NAME,
      value: terminal.terminalName,
      isMobile,
      isNeedEncodeCookie: true,
      flags: { epicOmni1936 },
    });
    setUserSelectedInfo({
      key: userSelectedConstants.TERMINAL_ID,
      value: terminal?.terminalId?.toString(),
      isMobile,
      isNeedEncodeCookie: true,
      flags: { epicOmni1936 },
    });
    setLocalStorageValue(
      userSelectedConstants.USER_LOCATION,
      JSON.stringify(location),
      isMobile,
    );
    setLocalStorageValue(
      userSelectedConstants.TERMINALS,
      JSON.stringify(terminals),
      isMobile,
    );
    setUserSelectedInfo({
      key: userSelectedConstants.IS_GET_GEOLOCATION,
      value: JSON.stringify(false),
      isMobile,
      isNeedEncodeCookie: true,
      flags: { epicOmni1936 },
    });
    setLocation(location);
    setCurrentTerminal(terminal);
    setCurrentTerminals(terminals);
    setIsGetCurrentLocation(false);
    if (!noNeedReload) {
      if (clearCacheAfterSwitchLocation) {
        clearCacheAndReloadPage(reloadUrl);
      } else {
        reloadPage(reloadUrl);
      }
    } else {
      cartServiceV2.setParams({
        terminalId: terminal.terminalId,
        terminal: terminal.terminalCode,
      });
    }
  };

  const getDistanceLocation = async ({
    address,
    lat,
    long,
  }: {
    address?: string;
    lat?: number;
    long?: number;
  }) => {
    try {
      setLoading(true);
      const response = await orderService.getTerminalsByAddress({
        fullAddress: address,
        lat,
        long,
        platformId,
      });
      setLoading(false);
      return response.data.terminals;
    } catch (error) {
      setLoading(false);
      return [];
    }
  };

  const setNewTerminal = (
    terminal: ISupermarketTerminal,
    noNeedReload?: boolean,
    reloadUrl?: string,
  ) => {
    if (!terminal) {
      return;
    }
    selectTerminal(terminal);
    if (!noNeedReload) {
      if (clearCacheAfterSwitchLocation) {
        clearCacheAndReloadPage(reloadUrl);
      } else {
        reloadPage(reloadUrl);
      }
    }
  };

  const getDataFromLocalStorage = () => {
    const terminalCodeFromStorage = getUserSelectedInfo(
      userSelectedConstants.TERMINAL,
      isMobile,
      true,
    );
    const terminalsFromStorage = getLocalStorageValue(
      userSelectedConstants.TERMINALS,
      isMobile,
    );
    const isGetGeoLocationFromStorage = getUserSelectedInfo(
      userSelectedConstants.IS_GET_GEOLOCATION,
      isMobile,
      true,
    );
    const jsonUserLocationFromStorage = getLocalStorageValue(
      userSelectedConstants.USER_LOCATION,
      isMobile,
    );

    return {
      terminalCodeFromStorage,
      terminalsFromStorage,
      isGetGeoLocationFromStorage,
      jsonUserLocationFromStorage,
    };
  };

  const getAndSaveTerminalFromStorage = async () => {
    trackCustomEvent('Start get data from storage', {
      action: 'get data from terra',
      label: 'Huy Test',
    });
    if (TerraKit.isAvailable() && isAllowGetTerminalFromTerra) {
      trackCustomEvent(
        'Start get terminal Data FromApp when turn on flag 1584',
        {
          action: 'getData',
          label: JSON.stringify(terminalId),
        },
      );
      const terminalDataFromApp = await TerraKit.erp().getTerminal();
      logger.log(`terminalDataFromApp: ${JSON.stringify(terminalDataFromApp)}`);
      trackCustomEvent('Terminal Data FromApp when turn on flag 1584', {
        action: 'get data from terra',
        label: JSON.stringify(terminalDataFromApp),
      });
      const hasAppData = !isEmptyObject(terminalDataFromApp);

      if (hasAppData && terminalId !== terminalDataFromApp.id) {
        trackCustomEvent(
          'Start save Terminal Data FromApp when turn on flag 1584',
          {
            action: 'get data from terra',
            label: `${terminalDataFromApp} + ${terminalDataFromApp.id}`,
          },
        );
        setNewTerminal({
          terminalCode: terminalDataFromApp.code,
          terminalId: terminalDataFromApp.id,
          terminalName: terminalDataFromApp.name || '',
          siteId: 0,
          fullAddress: '',
          distance: 0,
          provinceCode: '',
        });
      } else if (!hasAppData || (hasAppData && !terminalDataFromApp.id)) {
        // fallback: no terminal / terminalId from app -> take terminalId, terminalCode from URL query
        const { terminalId: terminalIdFromUrl, terminalCode } = getQueryParams(
          window?.location?.search,
          { terminalId: NumberParam, terminalCode: StringParam },
        );
        trackCustomEvent('missing terminalDataFromApp', {
          action: 'missing terminalDataFromApp',
          label: JSON.stringify({ terminalDataFromApp, terminalCode }),
        });
        if (!!terminalIdFromUrl && terminalId !== terminalIdFromUrl) {
          setNewTerminal({
            terminalCode: terminalCode || '',
            terminalId: terminalIdFromUrl,
            terminalName: '',
            siteId: 0,
            fullAddress: '',
            distance: 0,
            provinceCode: '',
          });
        }
      }
      return;
    }
    try {
      setLoadingGetDataFromStorage(true);
      const {
        terminalCodeFromStorage,
        terminalsFromStorage,
        isGetGeoLocationFromStorage,
        jsonUserLocationFromStorage,
      } = getDataFromLocalStorage();
      if (!!isGetGeoLocationFromStorage) {
        setIsGetCurrentLocation(getJson(isGetGeoLocationFromStorage));
      }
      if (!!terminalsFromStorage) {
        setCurrentTerminals(getJson(terminalsFromStorage) || []);
      }

      if (!!jsonUserLocationFromStorage) {
        const userLocationFromStorage = getJson(jsonUserLocationFromStorage);
        if (!!userLocationFromStorage) {
          setLocation(userLocationFromStorage);
        }
      }

      if (!!terminalCodeFromStorage && !!terminalsFromStorage) {
        const terminals = getJson(terminalsFromStorage) || [];
        const terminal = terminals.find(
          (item: ISupermarketTerminal) =>
            item.terminalCode === terminalCodeFromStorage,
        );
        setCurrentTerminal(terminal);
      }
      if (!!terminalsFromStorage && !!jsonUserLocationFromStorage) {
        const userLocationFromStorage = getJson(jsonUserLocationFromStorage);
        const terminals = getJson(terminalsFromStorage) || [];

        setTemporaryData({
          location: userLocationFromStorage,
          terminals: terminals || [],
        });
      }
      setLoadingGetDataFromStorage(false);
    } catch (error) {
      setLoadingGetDataFromStorage(false);
    }
  };

  useEffect(() => {
    if (epicOmni1825) {
      if ('geolocation' in navigator && 'permissions' in navigator) {
        navigator.permissions
          .query({ name: 'geolocation' })
          .then((result) => {
            setPermissionStatus(result.state); // 'granted', 'prompt', or 'denied'
          })
          .catch(() => {
            setPermissionStatus('denied');
          });
      } else {
        setPermissionStatus('denied');
      }
      if (!isDmsFlow) {
        getAndSaveTerminalFromStorage();
      }
      return;
    }
    const { terminalsFromStorage } = getDataFromLocalStorage();
    // only remove location & terminal info and reload page If level location is set before (epicOmni1825 were enabled before)
    if (!!terminalsFromStorage && !epicOmni1825) {
      removeUserSelectedInfo(
        userSelectedConstants.IS_GET_GEOLOCATION,
        isMobile,
      );
      removeUserSelectedInfo(userSelectedConstants.TERMINALS, isMobile);

      reloadPage();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [epicOmni1825, isDmsFlow, isMobile]);

  useEffect(() => {
    if (!!user?.userId) {
      setUserSelectedInfo({
        key: userSelectedConstants.IS_GET_GEOLOCATION,
        value: JSON.stringify(false),
        isMobile,
        isNeedEncodeCookie: true,
        flags: { epicOmni1936 },
      });
      setIsGetCurrentLocation(false);
    }
  }, [isMobile, user]);

  const onEditAddress = () => {
    onOpenTerminalPicker?.();

    setModeTerminalPicker(
      !!user?.userId
        ? MODE_TERMINAL_PICKER.LIST_ADDRESS
        : !![location.address, location.wardName, location.provinceName].join(
            '',
          )
        ? MODE_TERMINAL_PICKER.LIST_ADDRESS
        : MODE_TERMINAL_PICKER.ENTER_ADDRESS,
    );
  };

  return (
    <DistanceTerminalContext.Provider
      value={{
        setLoadingGetDistanceTerminals: setLoading,
        isLoadingGetDistanceTerminals: loading,
        setLocationGetDistanceTerminals: setLocation,
        locationGetDistanceTerminals: location,
        currentTerminal,
        currentTerminals,
        showTerminalPicker,
        onOpenTerminalPicker,
        onCloseTerminalPicker,
        isGetCurrentLocation,
        setIsGetCurrentLocation,
        getDistanceLocation,
        isModeEnterAddress,
        setIsModeEnterAddress,
        modeTerminalPicker,
        setModeTerminalPicker,
        setNewTerminal,
        messageError,
        setMessageError,
        setLoadingGetDistanceTerminal,
        overrideTerminal,
        permissionGetLocationStatus: permissionStatus,
        temporaryDataDistanceTerminal: temporaryData,
        setTemporaryDataDistanceTerminal: setTemporaryData,
        location,
        onEditAddress,
        loadingGetDataFromStorage,
        overrideLocation,
        resetSelectedAddress,
        overrideTerminals,
        editingAddress,
        setEditingAddress,
      }}
    >
      {children}
      {!isDmsFlow && !!epicOmni1825 && (
        <PopupTerminalPicker
          showPopup={showTerminalPicker}
          onClosePopup={onCloseTerminalPicker}
        />
      )}
    </DistanceTerminalContext.Provider>
  );
};

export const useDistanceTerminalContext = () => {
  return useContext(DistanceTerminalContext);
};
